import L from "leaflet";
import "mapbox-gl-leaflet";
import "mapbox-gl/dist/mapbox-gl.css";

import { LanguageKey } from "@/types/generated/LanguageKey";

// EXPERIMENT — vector basemap under Leaflet.
// Mapbox light-v11 vector style, recolored at runtime with the Sensolus design
// tokens so the basemap recedes behind markers/geozones ("Studio route in code":
// same effect as a custom Mapbox Studio theme, but versioned here in git).
// Wrapped as a Leaflet layer by mapbox-gl-leaflet so all existing Leaflet
// overlays (clusters, geozones, draw, playback) keep working on top.
//
// TODO before productionizing:
// - route style/tiles/glyphs through the backend map proxy (STIC-15578) via
//   transformRequest instead of a browser-side token
// - decide raster fallback for browsers without WebGL
const DEV_FALLBACK_TOKEN =
  "pk.eyJ1IjoiamFuLXNlbnNvbHVzIiwiYSI6ImNrN2JpMW92ejAwMGszbm92anppbzk0MXgifQ.saRkIIeZfUFV0e3jxdT3gw";

// Values from src/sass/custom-variables.scss — keep in sync with the tokens.
const TOKENS = {
  bgZebra: "#f9fafa", // $Sensolus_BG_Zebra
  bgLightGrey: "#eaeef1", // $Sensolus_BG_LightGrey
  bgDarkGrey: "#e7e7e1", // $Sensolus_BG_DarkGrey
  greyLightest: "#dee4e6", // $Sensolus_Grey_Lightest
  greyLighter: "#d5d9e0", // $Sensolus_Grey_Lighter
  greyLight: "#b8bfca", // $Sensolus_Grey_Light
  grey: "#535E6F", // $Sensolus_Grey
  greyDarker: "#424b59", // $Sensolus_Grey_Darker
  blueLighter: "#c2d7e4", // $Sensolus_Blue_Lighter
  blueLight: "#a0c3d8", // $Sensolus_Blue_Light
  yellow: "#ffcc66", // $Sensolus_Yellow
  // DERIVED, not an official token: $Sensolus_Green (#39cb99) washed ~90%
  // toward the background so greenery keeps a hint of green. Needs design
  // sign-off (or a real token) before this ships.
  greenWash: "#dfeee8",
};

// [style layer id in light-v11, paint property, Sensolus color or expression]
// Land is near-white so the tinted landuse/water/buildings read against it.
// [layerId, paint-property, value] — value is a color string or a Mapbox GL
// style expression (a nested array), e.g. a "match" on feature class.
type GlTint = [string, string, string | unknown[]];

const SENSOLUS_BASEMAP_TINTS: GlTint[] = [
  ["land", "background-color", TOKENS.bgZebra],
  // Green landuse classes keep a hint of green; the rest goes neutral gray
  [
    "landuse",
    "fill-color",
    [
      "match",
      ["get", "class"],
      ["park", "grass", "pitch", "cemetery", "wood", "scrub"],
      TOKENS.greenWash,
      TOKENS.bgDarkGrey,
    ],
  ],
  ["national-park", "fill-color", TOKENS.greenWash],
  ["water", "fill-color", TOKENS.blueLighter],
  ["waterway", "line-color", TOKENS.blueLighter],
  ["building", "fill-color", TOKENS.greyLighter],
  // Roads: flat soft gray instead of light-v11's white (which glowed against
  // the tinted land). Flattens the motorway/street hierarchy a bit — acceptable.
  ["road-simple", "line-color", TOKENS.greyLightest],
  ["bridge-simple", "line-color", TOKENS.greyLightest],
  ["bridge-case-simple", "line-color", TOKENS.greyLighter],
  ["tunnel-simple", "line-color", TOKENS.bgLightGrey],
  ["admin-0-boundary", "line-color", TOKENS.greyLight],
  ["admin-1-boundary", "line-color", TOKENS.greyLight],
  ["water-point-label", "text-color", TOKENS.blueLight],
  ["water-line-label", "text-color", TOKENS.blueLight],
  ["waterway-label", "text-color", TOKENS.blueLight],
  ["poi-label", "text-color", TOKENS.grey],
  ["settlement-subdivision-label", "text-color", TOKENS.grey],
  ["settlement-minor-label", "text-color", TOKENS.greyDarker],
  ["settlement-major-label", "text-color", TOKENS.greyDarker],
  ["state-label", "text-color", TOKENS.greyLight],
  ["country-label", "text-color", TOKENS.greyDarker],
];

// Transport theme: identical muted basemap, but the road hierarchy pops so
// dispatchers can tell motorways from local streets at a glance.
// streets-v8 road classes: motorway, trunk, primary, secondary, tertiary, ...
const roadHierarchy = (minorColor: string) => [
  "match",
  ["get", "class"],
  ["motorway", "motorway_link", "trunk", "trunk_link"],
  TOKENS.yellow,
  ["primary"],
  TOKENS.greyLight,
  ["secondary", "tertiary"],
  TOKENS.greyLighter,
  minorColor,
];

const TRANSPORT_BASEMAP_TINTS: GlTint[] = [
  ...SENSOLUS_BASEMAP_TINTS.filter(
    ([id]) => !["road-simple", "bridge-simple", "tunnel-simple"].includes(id)
  ),
  ["road-simple", "line-color", roadHierarchy(TOKENS.greyLightest)],
  ["bridge-simple", "line-color", roadHierarchy(TOKENS.greyLightest)],
  ["tunnel-simple", "line-color", roadHierarchy(TOKENS.bgLightGrey)],
];

const THEMES: Record<string, GlTint[]> = {
  sensolus: SENSOLUS_BASEMAP_TINTS,
  transport: TRANSPORT_BASEMAP_TINTS,
};

// Dev toggle while experimenting: localStorage.setItem("snt-map-theme", "sensolus")
// (or "transport") + reload. Transport is the default — road hierarchy matters
// to our (largely logistics) customers.
const activeTheme = (): GlTint[] => {
  const name =
    import.meta.env.VITE_MAPBOX_GL_THEME ||
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("snt-map-theme")) ||
    "transport";
  return THEMES[name] || TRANSPORT_BASEMAP_TINTS;
};

// Also used by the pure-GL 3D mode (Gl3dMapView) so both engines share the theme.
// `theme`: a THEMES key applies that tint set; `null` applies NO tints (raw
// Mapbox style); `undefined` falls back to the env/localStorage dev toggle
// (legacy behavior kept for the Gl3dMapView caller).
export const applySensolusTints = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  glMap: any,
  theme?: keyof typeof THEMES | null
) => {
  const tints =
    theme === null ? [] : theme ? THEMES[theme] || [] : activeTheme();
  tints.forEach(([layerId, prop, color]) => {
    if (glMap.getLayer(layerId)) {
      glMap.setPaintProperty(layerId, prop, color);
    }
  });
};

export const getMapboxGlToken = (): string =>
  import.meta.env.VITE_MAPBOX_GL_TOKEN || DEV_FALLBACK_TOKEN;

export const VECTOR_GL_STYLE = "mapbox://styles/mapbox/light-v11";

// User-selectable Street basemap styles. Each entry pairs a Mapbox GL style with
// the Sensolus tint treatment applied on top (`theme: null` = raw Mapbox style,
// no tints). Exposed in Configure view → Map Layers → Standard layers.
export type StreetStyleKey = "default" | "colorful" | "light" | "dark";

export const STREET_STYLES: {
  key: StreetStyleKey;
  glStyle: string;
  theme: keyof typeof THEMES | null;
  labelKey: LanguageKey;
}[] = [
  {
    key: "default",
    glStyle: "mapbox://styles/mapbox/light-v11",
    theme: "transport",
    labelKey: LanguageKey.default_key,
  },
  {
    key: "colorful",
    glStyle: "mapbox://styles/mapbox/streets-v12",
    theme: null,
    labelKey: LanguageKey.colorful_key,
  },
  {
    key: "light",
    glStyle: "mapbox://styles/mapbox/light-v11",
    theme: null,
    labelKey: LanguageKey.light_key,
  },
  {
    key: "dark",
    glStyle: "mapbox://styles/mapbox/dark-v11",
    theme: null,
    labelKey: LanguageKey.dark_key,
  },
];

export const DEFAULT_STREET_STYLE: StreetStyleKey = "default";

const streetStyleEntry = (key?: string) =>
  STREET_STYLES.find((s) => s.key === key) || STREET_STYLES[0];

// Resolve a (possibly null/unknown) style key to its { glStyle, theme }. Used by
// the pure-GL 3D map (Gl3dMapView) to follow the user's street-style preference.
export const getStreetStyle = (key?: string) => streetStyleEntry(key);

// Preview tile for the picker: a real render of the style via the Mapbox Static
// Images API (same token the GL layer uses). `default` and `light` share
// light-v11, so their tiles look alike — the label beneath disambiguates.
export const streetStyleThumbnailUrl = (styleKey: StreetStyleKey): string => {
  const styleId = streetStyleEntry(styleKey).glStyle.replace(
    "mapbox://styles/",
    ""
  );
  // A recognizable urban area at a modest zoom.
  const lon = 4.35;
  const lat = 50.85;
  const zoom = 11;
  return `https://api.mapbox.com/styles/v1/${styleId}/static/${lon},${lat},${zoom}/120x120@2x?access_token=${getMapboxGlToken()}&attribution=false&logo=false`;
};

// Satellite basemap for the pure-GL map (Gl3dMapView). Reuses the exact backend
// map-proxy raster tiles the 2D map serves for its "Satellite" base layer (see
// LayerUnwired.ts satellite branch) — keys are injected server-side, so no
// Mapbox token is needed here either. GL doesn't interpolate the {s} subdomain
// placeholder, so we expand it into a/b/c tile URLs. Retina serves 512px @2x
// tiles, matching LayerUnwired.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const buildSatelliteRasterSource = (): any => {
  const retina =
    typeof window !== "undefined" && window.devicePixelRatio > 1 ? "@2x" : "";
  return {
    type: "raster",
    tileSize: retina ? 512 : 256,
    tiles: ["a", "b", "c"].map(
      (s) =>
        `/map-proxy/satellite/${s}/v4/mapbox.satellite/{z}/{x}/{y}${retina}.png`
    ),
    attribution:
      'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
  };
};

export const LayerVectorGL = (
  styleKey: StreetStyleKey = DEFAULT_STREET_STYLE
) => {
  let entry = streetStyleEntry(styleKey);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layer = (L as any).mapboxGL({
    style: entry.glStyle,
    // Leaflet derives map.getMaxZoom() from its layers (the raster layer set 22).
    // Without this it returns Infinity and every project()/unproject() against
    // getMaxZoom() yields NaN (e.g. the recenter-on-select pan). GL-side this is
    // the default anyway. Don't add minZoom here: it would clamp the GL camera
    // (which runs at leaflet zoom - 1) — the map's minZoom is set in CommonMapReact.
    maxZoom: 22,
    // Leaflet projects overlays on flat Mercator; without this, GL v3 switches to
    // a 3D globe at low zoom and markers drift off the map ("becomes fiction")
    projection: "mercator",
    // No oversized GL canvas: keep the GL viewport identical to Leaflet's so
    // GL's world-edge clamping matches Leaflet's maxBounds exactly (no drift
    // at min zoom). Costs a sliver of gray at the edges during fast pans.
    padding: 0,
    accessToken: getMapboxGlToken(),
    attribution:
      '&copy; <a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> Contributors',
  });

  const applyCurrentTheme = () => {
    const glMap = layer.getMapboxMap();
    if (!glMap) {
      return;
    }
    if (glMap.isStyleLoaded()) {
      applySensolusTints(glMap, entry.theme);
    } else {
      glMap.once("style.load", () => applySensolusTints(glMap, entry.theme));
    }
  };

  layer.on("add", applyCurrentTheme);

  // Live style switching without rebuilding the Leaflet layer (called by
  // ControlLayers when the persisted `streetStyle` changes). Updating `entry`
  // first means the "add" handler applies the right tints even when the GL map
  // does not exist yet (e.g. Street not currently the active base layer).
  layer.setSntStreetStyle = (nextKey: StreetStyleKey) => {
    const next = streetStyleEntry(nextKey);
    if (next.key === entry.key) {
      return;
    }
    entry = next;
    layer.sntStreetStyle = next.key;
    const glMap = layer.getMapboxMap();
    if (!glMap) {
      return;
    }
    glMap.setStyle(next.glStyle);
    glMap.once("style.load", () => applySensolusTints(glMap, next.theme));
  };
  layer.sntStreetStyle = entry.key;

  return layer;
};
